import { defineMessages } from 'react-intl';

/**
 * The copy of the lyric timing in the song files sheet and on the lyric editor page (designs
 * release-kit s8, s8b, s11; spec 2026-10-05 §5.8, §5.9).
 */
export const lyricTimingMessages = defineMessages({
  title: { id: 'app.sml.catalog.song.lyricTiming.title', defaultMessage: 'Lyric timing' },
  pillSynced: { id: 'app.sml.catalog.song.lyricTiming.pill.synced', defaultMessage: 'Synced' },
  pillSyncing: { id: 'app.sml.catalog.song.lyricTiming.pill.syncing', defaultMessage: 'Syncing' },
  pillFailed: { id: 'app.sml.catalog.song.lyricTiming.pill.failed', defaultMessage: 'Failed' },
  pillNoLyrics: { id: 'app.sml.catalog.song.lyricTiming.pill.noLyrics', defaultMessage: 'No lyrics' },
  pillNoMaster: { id: 'app.sml.catalog.song.lyricTiming.pill.noMaster', defaultMessage: 'No master' },
  pillNotSynced: { id: 'app.sml.catalog.song.lyricTiming.pill.notSynced', defaultMessage: 'Not synced' },
  syncedAgo: { id: 'app.sml.catalog.song.lyricTiming.syncedAgo', defaultMessage: 'Synced {ago}' },
  changedByYou: {
    id: 'app.sml.catalog.song.lyricTiming.changedByYou',
    defaultMessage: '{count, plural, one {# changed by you} other {# changed by you}}',
  },
  toCheck: {
    id: 'app.sml.catalog.song.lyricTiming.toCheck',
    defaultMessage: '{count, plural, one {# to check} other {# to check}}',
  },
  lineCount: {
    id: 'app.sml.catalog.song.lyricTiming.lineCount',
    defaultMessage: '{count, plural, one {# line} other {# lines}}',
  },
  noTimeCount: {
    id: 'app.sml.catalog.song.lyricTiming.noTimeCount',
    defaultMessage: '{count, plural, one {# with no time} other {# with no time}}',
  },
  syncing: {
    id: 'app.sml.catalog.song.lyricTiming.syncing',
    defaultMessage: 'We are matching the lyrics to the vocals. About a minute.',
  },
  syncingLines: {
    id: 'app.sml.catalog.song.lyricTiming.syncingLines',
    defaultMessage: 'The lines show without times until then.',
  },
  lyricsChanged: {
    id: 'app.sml.catalog.song.lyricTiming.lyricsChanged',
    defaultMessage:
      'The lyrics changed, so we sync them again. {count, plural, one {Your # change stays on the lines that did not change.} other {Your # changes stay on the lines that did not change.}}',
  },
  failNoVocals: {
    id: 'app.sml.catalog.song.lyricTiming.fail.noVocals',
    defaultMessage: 'We could not hear a voice in the song.',
  },
  failNoMatch: {
    id: 'app.sml.catalog.song.lyricTiming.fail.noMatch',
    defaultMessage: 'We could not hear these lyrics in the song.',
  },
  failGeneral: {
    id: 'app.sml.catalog.song.lyricTiming.fail.general',
    defaultMessage: 'We could not sync the lyrics to the master.',
  },
  failHint: {
    id: 'app.sml.catalog.song.lyricTiming.fail.hint',
    defaultMessage: 'Check that the lyrics belong to this master, then re-sync. You can also set each time by hand.',
  },
  noLyrics: {
    id: 'app.sml.catalog.song.lyricTiming.noLyrics',
    defaultMessage: 'Add the lyrics of the song. We sync them to the master. Lyric videos and lyric moments need them.',
  },
  noMaster: {
    id: 'app.sml.catalog.song.lyricTiming.noMaster',
    defaultMessage: 'Add the master first. We sync the lyrics to it.',
  },
  masterNotReady: {
    id: 'app.sml.catalog.song.lyricTiming.masterNotReady',
    defaultMessage: 'We sync the lyrics when MUSIC24 has read the master.',
  },
  masterFailed: {
    id: 'app.sml.catalog.song.lyricTiming.masterFailed',
    defaultMessage: 'MUSIC24 cannot read this master. Upload it again, then we sync the lyrics.',
  },
  notSynced: {
    id: 'app.sml.catalog.song.lyricTiming.notSynced',
    defaultMessage: 'The lyrics are not synced to the master yet.',
  },
  addLyrics: { id: 'app.sml.catalog.song.lyricTiming.addLyrics', defaultMessage: 'Add the lyrics' },
  resync: { id: 'app.sml.catalog.song.lyricTiming.resync', defaultMessage: 'Re-sync' },
  syncNow: { id: 'app.sml.catalog.song.lyricTiming.syncNow', defaultMessage: 'Sync now' },
  byHand: { id: 'app.sml.catalog.song.lyricTiming.byHand', defaultMessage: 'Set times by hand' },
  byHandHint: {
    id: 'app.sml.catalog.song.lyricTiming.byHandHint',
    defaultMessage: 'Play the master and press T on each line, or tap ▶ and use − and +.',
  },
  legendChanged: { id: 'app.sml.catalog.song.lyricTiming.legend.changed', defaultMessage: 'changed by you' },
  legendCheck: {
    id: 'app.sml.catalog.song.lyricTiming.legend.check',
    defaultMessage: 'check it: the sync is not sure',
  },
  keyPlay: { id: 'app.sml.catalog.song.lyricTiming.key.play', defaultMessage: 'play' },
  keyLine: { id: 'app.sml.catalog.song.lyricTiming.key.line', defaultMessage: 'line' },
  keyNudge: {
    id: 'app.sml.catalog.song.lyricTiming.key.nudge',
    defaultMessage: 'nudge 0.1 s, with {shift} 0.5 s',
  },
  keySet: {
    id: 'app.sml.catalog.song.lyricTiming.key.set',
    defaultMessage: 'set the line to the play position',
  },
  tapToPlay: { id: 'app.sml.catalog.song.lyricTiming.tapToPlay', defaultMessage: 'Tap ▶ to play from a line.' },
  listLabel: { id: 'app.sml.catalog.song.lyricTiming.listLabel', defaultMessage: 'The lyric lines and their times' },
  playFrom: { id: 'app.sml.catalog.song.lyricTiming.playFrom', defaultMessage: 'Play from {time}' },
  playFromNoTime: {
    id: 'app.sml.catalog.song.lyricTiming.playFromNoTime',
    defaultMessage: 'This line has no time yet',
  },
  earlier: { id: 'app.sml.catalog.song.lyricTiming.earlier', defaultMessage: 'Start this line 0.1 s earlier' },
  later: { id: 'app.sml.catalog.song.lyricTiming.later', defaultMessage: 'Start this line 0.1 s later' },
  dotChanged: { id: 'app.sml.catalog.song.lyricTiming.dot.changed', defaultMessage: 'Changed by you' },
  dotCheck: { id: 'app.sml.catalog.song.lyricTiming.dot.check', defaultMessage: 'Check this time' },
  saving: { id: 'app.sml.catalog.song.lyricTiming.saving', defaultMessage: 'Saving…' },
  // The refusals of the edits and of Re-sync.
  orderError: {
    id: 'app.sml.catalog.song.lyricTiming.error.order',
    defaultMessage: 'A line must start after the line above it and inside the song.',
  },
  staleError: {
    id: 'app.sml.catalog.song.lyricTiming.error.stale',
    defaultMessage: 'Someone else changed the times. We show their times now. Make your change again.',
  },
  syncingError: {
    id: 'app.sml.catalog.song.lyricTiming.error.syncing',
    defaultMessage: 'The lyrics are syncing now. Change the times when the sync ends.',
  },
  saveError: {
    id: 'app.sml.catalog.song.lyricTiming.error.save',
    defaultMessage: 'We could not save the time. Try again.',
  },
  resyncLimit: {
    id: 'app.sml.catalog.song.lyricTiming.error.resyncLimit',
    defaultMessage: 'You re-synced this song {count} times this hour. Try again later.',
  },
  resyncNoLyrics: {
    id: 'app.sml.catalog.song.lyricTiming.error.noLyrics',
    defaultMessage: 'Add the lyrics of the song first.',
  },
  resyncNoMaster: {
    id: 'app.sml.catalog.song.lyricTiming.error.noMaster',
    defaultMessage: 'Add the master of the song first.',
  },
  resyncMasterProcessing: {
    id: 'app.sml.catalog.song.lyricTiming.error.masterProcessing',
    defaultMessage: 'MUSIC24 still reads the master. Try again in a minute.',
  },
  resyncMasterFailed: {
    id: 'app.sml.catalog.song.lyricTiming.error.masterFailed',
    defaultMessage: 'MUSIC24 cannot read this master. Upload it again.',
  },
  resyncDone: {
    id: 'app.sml.catalog.song.lyricTiming.resyncDone',
    defaultMessage: 'Synced again.',
  },
  resyncDoneSame: {
    id: 'app.sml.catalog.song.lyricTiming.resyncDoneSame',
    defaultMessage: 'Synced again. The times did not change.',
  },
  resyncError: {
    id: 'app.sml.catalog.song.lyricTiming.error.resync',
    defaultMessage: 'The sync did not start. Try again.',
  },
  // The confirm of Re-sync after edits by hand (design s8).
  confirmTitle: { id: 'app.sml.catalog.song.lyricTiming.confirm.title', defaultMessage: 'Re-sync the lyrics?' },
  confirmBody: {
    id: 'app.sml.catalog.song.lyricTiming.confirm.body',
    defaultMessage:
      '{count, plural, one {Re-sync drops your # change.} other {Re-sync drops your # changes.}} The times come from the master again.',
  },
  confirmAction: { id: 'app.sml.catalog.song.lyricTiming.confirm.action', defaultMessage: 'Re-sync' },
  // The lyric editor page (`/app/catalog/catalogLyrics`).
  pageNotFound: {
    id: 'app.sml.catalog.song.lyricTiming.page.notFound',
    defaultMessage: 'We could not find this song in your catalog.',
  },
  pageBack: { id: 'app.sml.catalog.song.lyricTiming.page.back', defaultMessage: 'Open the Catalog' },
});
